/**
 * Calendario laboral chileno: lun–vie menos feriados legales.
 * Incluye Viernes/Sábado Santo (Pascua) y feriados fijos / movibles conocidos.
 */

const pad2 = (n) => String(n).padStart(2, '0')

export function toISODate(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return ''
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
}

export function parseISODate(iso) {
  const s = String(iso || '').slice(0, 10)
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s)
  if (!m) return null
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
}

/** Clave civil YYYYMMDD. Evita que el cambio de hora (septiembre) deje un día fuera al comparar Date. */
export function ymdKey(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return 0
  return d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate()
}

/** Fecha "hoy" en America/Santiago (calendario civil). */
export function fechaHoySantiagoISO() {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Santiago',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date())
  const get = (type) => parts.find((p) => p.type === type)?.value
  return `${get('year')}-${get('month')}-${get('day')}`
}

/** Cierre del día anterior (nunca el día en curso). */
export function fechaCorteAnalisisISO(hoyISO = fechaHoySantiagoISO()) {
  const d = parseISODate(hoyISO)
  if (!d) return ''
  d.setDate(d.getDate() - 1)
  return toISODate(d)
}

/** Algoritmo de Meeus/Jones/Butcher — Domingo de Pascua (calendario gregoriano). */
function easterSunday(year) {
  const a = year % 19
  const b = Math.floor(year / 100)
  const c = year % 100
  const d = Math.floor(b / 4)
  const e = b % 4
  const f = Math.floor((b + 8) / 25)
  const g = Math.floor((b - f + 1) / 3)
  const h = (19 * a + b - d - g + 15) % 30
  const i = Math.floor(c / 4)
  const k = c % 4
  const l = (32 + 2 * e + 2 * i - h - k) % 7
  const m = Math.floor((a + 11 * h + 22 * l) / 451)
  const month = Math.floor((h + l - 7 * m + 114) / 31)
  const day = ((h + l - 7 * m + 114) % 31) + 1
  return new Date(year, month - 1, day)
}

function addDays(d, n) {
  const out = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  out.setDate(out.getDate() + n)
  return out
}

/**
 * Feriados legales Chile por año (ISO yyyy-mm-dd).
 * Incluye feriados fijos y Pascua; años 2025–2027 con fechas oficiales conocidas
 * y generación genérica para otros años cercanos.
 */
export function feriadosChileISO(year) {
  const y = Number(year)
  if (!Number.isFinite(y)) return new Set()

  const fixed = [
    `${y}-01-01`, // Año Nuevo
    `${y}-05-01`, // Día del Trabajo
    `${y}-05-21`, // Glorias Navales
    `${y}-06-21`, // Pueblos Indígenas
    `${y}-06-29`, // San Pedro y San Pablo
    `${y}-07-16`, // Virgen del Carmen
    `${y}-08-15`, // Asunción de la Virgen
    `${y}-09-18`, // Independencia
    `${y}-09-19`, // Glorias del Ejército
    `${y}-10-12`, // Encuentro de Dos Mundos
    `${y}-10-31`, // Iglesias Evangélicas
    `${y}-11-01`, // Todos los Santos
    `${y}-12-08`, // Inmaculada Concepción
    `${y}-12-25`, // Navidad
  ]

  const easter = easterSunday(y)
  const viernesSanto = toISODate(addDays(easter, -2))
  const sabadoSanto = toISODate(addDays(easter, -1))

  // Ajustes / extras oficiales por año (cuando difieren del listado fijo)
  const extrasByYear = {
    2025: ['2025-04-18', '2025-04-19'], // Viernes/Sábado Santo 2025
    2026: [], // Pascua ya cubierta por cálculo
    2027: [],
  }

  const set = new Set([...fixed, viernesSanto, sabadoSanto, ...(extrasByYear[y] || [])])
  return set
}

export function isChileanHoliday(isoOrDate) {
  const iso = typeof isoOrDate === 'string' ? String(isoOrDate).slice(0, 10) : toISODate(isoOrDate)
  const d = parseISODate(iso)
  if (!d) return false
  return feriadosChileISO(d.getFullYear()).has(iso)
}

/** Día hábil chileno: lun–vie y no feriado. */
export function isChileanBusinessDay(isoOrDate) {
  const d = typeof isoOrDate === 'string' ? parseISODate(isoOrDate) : isoOrDate
  if (!d || Number.isNaN(d.getTime())) return false
  const wd = d.getDay()
  if (wd === 0 || wd === 6) return false
  return !isChileanHoliday(toISODate(d))
}

/**
 * Filtra series diarias (gráficos) a días hábiles Chile.
 * Acepta `fecha` en ISO `yyyy-mm-dd` o label `dd/mm` (con meta.desde/hasta o fechaConsulta).
 */
export function filterDailySeriesBusinessDaysChile(
  serie,
  { meta, fechaConsulta, fechaKey = 'fecha' } = {},
) {
  const rows = Array.isArray(serie) ? serie : []
  if (!rows.length) return []

  const sample = String(rows[0]?.[fechaKey] || '')
  if (/^\d{4}-\d{2}-\d{2}/.test(sample)) {
    return rows.filter((r) => isChileanBusinessDay(String(r?.[fechaKey] || '').slice(0, 10)))
  }

  const desde = parseISODate(meta?.desde || fechaConsulta)
  const hasta = parseISODate(meta?.hasta || fechaConsulta)
  if (!desde || !hasta) {
    const y = String(fechaConsulta || '').slice(0, 4)
    return rows.filter((r) => {
      const m = /^(\d{2})\/(\d{2})$/.exec(String(r?.[fechaKey] || ''))
      if (!m || !y) return true
      return isChileanBusinessDay(`${y}-${m[2]}-${m[1]}`)
    })
  }

  const byLabel = new Map(rows.map((r) => [String(r?.[fechaKey] || ''), r]))
  const out = []
  const cur = new Date(desde.getFullYear(), desde.getMonth(), desde.getDate())
  const end = new Date(hasta.getFullYear(), hasta.getMonth(), hasta.getDate())
  while (cur <= end) {
    const iso = toISODate(cur)
    if (isChileanBusinessDay(iso)) {
      const label = `${pad2(cur.getDate())}/${pad2(cur.getMonth() + 1)}`
      const hit = byLabel.get(label)
      if (hit) out.push(hit)
    }
    cur.setDate(cur.getDate() + 1)
  }
  return out
}

/** Cuenta días hábiles desde el 1 del mes hasta `throughISO` inclusive. */
export function countBusinessDaysThrough(year, month, throughISO) {
  const through = parseISODate(throughISO)
  if (!through) return 0
  const start = new Date(year, month - 1, 1)
  const throughKey = ymdKey(through)
  if (throughKey < ymdKey(start)) return 0
  let n = 0
  const d = new Date(year, month - 1, 1)
  while (ymdKey(d) <= throughKey && d.getMonth() === month - 1) {
    if (isChileanBusinessDay(d)) n += 1
    d.setDate(d.getDate() + 1)
  }
  return n
}

/**
 * Fecha del N-ésimo día hábil del mes (1-indexed).
 * Si el mes no tiene N hábiles, retorna el último hábil del mes.
 */
export function nthBusinessDayOfMonth(year, month, n) {
  const target = Math.max(1, Math.floor(Number(n) || 1))
  let count = 0
  let last = null
  const d = new Date(year, month - 1, 1)
  while (d.getMonth() === month - 1) {
    if (isChileanBusinessDay(d)) {
      count += 1
      last = new Date(d.getFullYear(), d.getMonth(), d.getDate())
      if (count === target) return last
    }
    d.setDate(d.getDate() + 1)
  }
  return last
}

/** N default = hábiles del mes de corte desde el 1 hasta fecha_corte. */
export function defaultBusinessDaysWindow(fechaCorteISO) {
  const d = parseISODate(fechaCorteISO)
  if (!d) return 1
  return Math.max(1, countBusinessDaysThrough(d.getFullYear(), d.getMonth() + 1, fechaCorteISO))
}

/** Máximo de hábiles en el mes de la fecha de corte. */
export function maxBusinessDaysInMonth(fechaCorteISO) {
  const d = parseISODate(fechaCorteISO)
  if (!d) return 1
  const y = d.getFullYear()
  const m = d.getMonth() + 1
  const last = new Date(y, m, 0)
  return Math.max(1, countBusinessDaysThrough(y, m, toISODate(last)))
}
